import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function SafetyGuidePage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Article Hero */}
      <section className="relative w-full pt-20 pb-8 md:pt-24 md:pb-12 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6">
            <Link to="/" className="hover:text-foreground-800 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-[10px]"></i>
            <span className="text-foreground-400">Guides</span>
            <i className="ri-arrow-right-s-line text-[10px]"></i>
            <span className="text-foreground-700 font-medium">Safety Guide</span>
          </nav>

          <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium tracking-wide mb-4">
            Safety &amp; Trust
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold text-foreground-950 leading-tight mb-4">
            Is Superbuy Spreadsheet Safe? The Complete 2026 Security Guide
          </h1>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
            A thorough, no-nonsense breakdown of Superbuy Spreadsheet safety — covering payment protection, seller verification, privacy practices, QC photo strategies, common scams, and how to shop with complete confidence on international platforms.
          </p>
          <div className="flex items-center gap-4 mt-6 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i> 10 min read
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-shield-check-line"></i> Verified &amp; Updated July 2026
            </span>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed space-y-8">

          {/* Section 1: The Big Question */}
          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
              So, Is Superbuy Spreadsheet Actually Safe?
            </h2>
            <p className="text-sm md:text-base leading-relaxed">
              Let us cut through the noise and give you a direct answer: <strong>yes, the Superbuy Spreadsheet itself is safe to use</strong>. The spreadsheet is essentially a community-curated Google Sheets document that organizes product links, category tags, sizing notes, and community feedback. It does not process payments, store personal information, or have any direct access to your accounts. The safety concern is not about the spreadsheet — it is about how carefully you approach the products and sellers listed within it.
            </p>
            <p className="text-sm md:text-base leading-relaxed">
              Think of the Superbuy Spreadsheet as a library catalog. The catalog itself cannot harm you. But the books — in this analogy, the individual products and sellers — vary in quality and reliability. This guide exists to teach you how to evaluate those products, protect your payments, and avoid the pitfalls that inexperienced shoppers sometimes fall into. If you are new here, start with our <Link to="/superbuy-spreadsheet-guide" className="text-primary-600 hover:text-primary-700 font-medium underline underline-offset-4">Superbuy Spreadsheet Complete Guide</Link> for a full overview before diving into safety specifics.
            </p>
          </section>

          {/* Section 2: Understanding the Risk Landscape */}
          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
              Understanding the Real Risks — And What Is Actually Safe
            </h2>
            <p className="text-sm md:text-base leading-relaxed">
              Before we dive into protection strategies, it is important to separate genuine concerns from exaggerated fears. Here is a realistic breakdown of what you should and should not worry about when using Superbuy Spreadsheet to discover products.
            </p>

            <h3 className="font-heading text-xl font-semibold text-foreground-900 mt-6 mb-3">What You Do NOT Need to Worry About</h3>
            <ul className="space-y-2 text-sm md:text-base">
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-accent-500 mt-1 shrink-0"></i>
                <span><strong>The spreadsheet stealing your data.</strong> It is a read-only Google Sheets document. It cannot install malware, extract passwords, or access anything on your device.</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-accent-500 mt-1 shrink-0"></i>
                <span><strong>Links directly charging your account.</strong> The links inside the spreadsheet lead to product pages where you must manually add items to a cart and complete checkout. Nothing happens automatically.</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-accent-500 mt-1 shrink-0"></i>
                <span><strong>Your personal information being leaked.</strong> The spreadsheet does not collect any personal data. It is purely a resource document maintained by a volunteer community.</span>
              </li>
            </ul>

            <h3 className="font-heading text-xl font-semibold text-foreground-900 mt-6 mb-3">What You SHOULD Pay Attention To</h3>
            <ul className="space-y-2 text-sm md:text-base">
              <li className="flex items-start gap-2">
                <i className="ri-error-warning-line text-primary-500 mt-1 shrink-0"></i>
                <span><strong>Seller reliability.</strong> Not every seller listed in the spreadsheet has the same quality standards. You need to verify seller ratings before purchasing.</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-error-warning-line text-primary-500 mt-1 shrink-0"></i>
                <span><strong>Product authenticity.</strong> Some listings may involve replica or inspired products. Understanding what you are buying is essential for avoiding disappointment.</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-error-warning-line text-primary-500 mt-1 shrink-0"></i>
                <span><strong>Payment method security.</strong> Always use the platform&rsquo;s official checkout system. Avoid sellers who ask for direct bank transfers or payments through unverified third-party services.</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-error-warning-line text-primary-500 mt-1 shrink-0"></i>
                <span><strong>Shipping logistics.</strong> International shipping introduces customs fees, carrier handoffs, and potential delays. Our <Link to="/superbuy-spreadsheet-shipping-guide" className="text-primary-600 hover:text-primary-700 font-medium underline underline-offset-4">Shipping Guide</Link> covers this in detail.</span>
              </li>
            </ul>
          </section>

          {/* Section 3: Seller Verification */}
          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
              How to Verify Sellers Before You Buy
            </h2>
            <p className="text-sm md:text-base leading-relaxed">
              Seller verification is the single most important safety habit you can develop. The Superbuy Spreadsheet may highlight trending products, but it is your responsibility to check each seller&rsquo;s reputation before placing an order. Here is a step-by-step verification checklist that experienced shoppers use.
            </p>

            <div className="rounded-lg bg-background-100 p-5 md:p-6 my-6">
              <h4 className="font-heading text-lg font-semibold text-foreground-900 mb-4">Seller Verification Checklist</h4>
              <ol className="space-y-3 text-sm md:text-base list-decimal list-inside">
                <li className="text-foreground-800"><strong>Check the seller&rsquo;s rating:</strong> Aim for sellers with at least 95% positive feedback. Anything below 90% is a red flag.</li>
                <li className="text-foreground-800"><strong>Review transaction volume:</strong> A high number of completed transactions (hundreds or thousands) indicates an established, reliable seller.</li>
                <li className="text-foreground-800"><strong>Read detailed feedback:</strong> Do not just look at the rating number. Scroll through recent reviews to see what buyers specifically say about product quality, shipping speed, and communication.</li>
                <li className="text-foreground-800"><strong>Examine product photos:</strong> Sellers using real photographs of the products — rather than only stock images — generally indicate greater transparency.</li>
                <li className="text-foreground-800"><strong>Check store age:</strong> An older store with consistent activity over months or years is more trustworthy than a brand-new account with limited history.</li>
                <li className="text-foreground-800"><strong>Look for detailed product descriptions:</strong> Sellers who include sizing charts, material specifications, and care instructions tend to be more professional and reliable.</li>
              </ol>
            </div>

            <p className="text-sm md:text-base leading-relaxed">
              If a seller fails two or more items on this checklist, consider looking for alternative listings. The Superbuy Spreadsheet often includes multiple sellers for popular product types, so you usually have options. Check out our <Link to="/superbuy-spreadsheet-categories" className="text-primary-600 hover:text-primary-700 font-medium underline underline-offset-4">Categories Guide</Link> for category-specific seller recommendations.
            </p>
          </section>

          {/* Section 4: Payment Safety */}
          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
              Payment Safety: Protecting Your Money
            </h2>
            <p className="text-sm md:text-base leading-relaxed">
              Payment security is where many newcomers make costly mistakes. The golden rule is simple: <strong>never send money outside the official platform&rsquo;s checkout system</strong>. Here is everything you need to know about keeping your transactions secure.
            </p>

            <h3 className="font-heading text-xl font-semibold text-foreground-900 mt-6 mb-3">Always Use the Platform&rsquo;s Built-in Payment System</h3>
            <p className="text-sm md:text-base leading-relaxed">
              Major international platforms offer buyer protection programs that cover you when items do not arrive, arrive damaged, or differ significantly from their description. When you pay through the official checkout, your money is held in escrow until you confirm receipt of the goods. If a seller asks you to pay through PayPal Friends &amp; Family, Western Union, cryptocurrency, or direct bank transfer — <strong>politely decline and walk away</strong>. These payment methods offer zero buyer protection, and once the money is sent, recovering it is nearly impossible.
            </p>

            <h3 className="font-heading text-xl font-semibold text-foreground-900 mt-6 mb-3">Use Credit Cards or Verified Payment Methods</h3>
            <p className="text-sm md:text-base leading-relaxed">
              Whenever possible, link a credit card rather than a debit card to your shopping account. Credit cards offer an additional layer of protection through chargeback rights. If the platform&rsquo;s dispute resolution process fails, you can often escalate the issue through your card issuer. Debit cards typically do not provide the same level of fraud protection, and recovering funds from a compromised debit account can take weeks.
            </p>

            <h3 className="font-heading text-xl font-semibold text-foreground-900 mt-6 mb-3">Watch Out for Fake Platform Links</h3>
            <p className="text-sm md:text-base leading-relaxed">
              Some malicious actors create phishing pages that look identical to legitimate shopping platforms. Always verify the URL in your browser&rsquo;s address bar before entering any login credentials or payment information. The Superbuy Spreadsheet links are community-curated, but it is still good practice to double-check you are on the correct domain before completing a purchase. If a link redirects you to an unexpected domain or asks you to re-enter login details, close the tab immediately.
            </p>
          </section>

          {/* Section 5: QC Photos */}
          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
              The QC Photo Advantage: See Before You Ship
            </h2>
            <p className="text-sm md:text-base leading-relaxed">
              Quality Check photos — commonly called QC photos in the community — are one of the most powerful safety tools available to international shoppers. When you use a shopping agent service, the agent receives your items at their warehouse and photographs them before forwarding the package to your international address. This gives you a critical window to inspect the actual product you will receive.
            </p>

            <div className="overflow-x-auto my-6">
              <table className="w-full text-xs md:text-sm border-collapse">
                <thead>
                  <tr className="border-b border-background-300">
                    <th className="text-left py-3 pr-4 font-semibold text-foreground-900">Without QC Photos</th>
                    <th className="text-left py-3 pl-4 font-semibold text-foreground-900">With QC Photos</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-background-200">
                    <td className="py-3 pr-4 text-foreground-700">Item ships directly to you unseen</td>
                    <td className="py-3 pl-4 text-foreground-700">Agent photographs item at warehouse first</td>
                  </tr>
                  <tr className="border-b border-background-200">
                    <td className="py-3 pr-4 text-foreground-700">No chance to catch defects</td>
                    <td className="py-3 pl-4 text-foreground-700">Spot defects, color mismatches, or wrong sizes</td>
                  </tr>
                  <tr className="border-b border-background-200">
                    <td className="py-3 pr-4 text-foreground-700">International return shipping is expensive</td>
                    <td className="py-3 pl-4 text-foreground-700">Return locally within the country for free or low cost</td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4 text-foreground-700">Must accept whatever arrives</td>
                    <td className="py-3 pl-4 text-foreground-700">Request exchange or refund before international shipping</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-sm md:text-base leading-relaxed">
              When reviewing QC photos, pay attention to stitching quality, logo placement, color accuracy compared to the listing photos, and any visible flaws. Do not hesitate to request additional photos if the initial set does not clearly show all angles. Legitimate agents are happy to provide extra shots — it is part of the service you are paying for.
            </p>
          </section>

          {/* Section 6: Common Scams */}
          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
              Common Scams and How to Spot Them
            </h2>
            <p className="text-sm md:text-base leading-relaxed">
              While the Superbuy Spreadsheet community actively tries to filter out bad actors, it is still possible to encounter questionable sellers. Knowing the most common scam patterns helps you avoid them proactively.
            </p>

            <div className="space-y-4 my-6">
              <div className="rounded-lg bg-primary-100/60 p-4">
                <h4 className="font-semibold text-foreground-900 text-sm mb-1">Bait-and-Switch Listings</h4>
                <p className="text-xs md:text-sm text-foreground-700 leading-relaxed">
                  A seller advertises a high-quality product but ships an inferior version. <strong>How to protect yourself:</strong> Request QC photos and compare them carefully against the original listing images. If the QC photos look different, refuse the item through your agent.
                </p>
              </div>
              <div className="rounded-lg bg-primary-100/60 p-4">
                <h4 className="font-semibold text-foreground-900 text-sm mb-1">Fake Tracking Numbers</h4>
                <p className="text-xs md:text-sm text-foreground-700 leading-relaxed">
                  A seller provides a tracking number that never updates or belongs to a different package. <strong>How to protect yourself:</strong> Only use platforms with escrow-style payment systems. Do not confirm receipt until the package actually arrives.
                </p>
              </div>
              <div className="rounded-lg bg-primary-100/60 p-4">
                <h4 className="font-semibold text-foreground-900 text-sm mb-1">Off-Platform Payment Requests</h4>
                <p className="text-xs md:text-sm text-foreground-700 leading-relaxed">
                  A seller offers a small discount if you pay outside the platform. <strong>How to protect yourself:</strong> Never accept this offer, no matter how tempting the discount. The small savings are not worth losing all buyer protections.
                </p>
              </div>
              <div className="rounded-lg bg-primary-100/60 p-4">
                <h4 className="font-semibold text-foreground-900 text-sm mb-1">Fake Review Accumulation</h4>
                <p className="text-xs md:text-sm text-foreground-700 leading-relaxed">
                  A seller has hundreds of identical generic reviews with no specific product details. <strong>How to protect yourself:</strong> Look for reviews that mention specific product details, sizing accuracy, and shipping timelines rather than vague compliments.
                </p>
              </div>
            </div>
          </section>

          {/* Section 7: Privacy */}
          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
              Privacy Protection When Shopping Internationally
            </h2>
            <p className="text-sm md:text-base leading-relaxed">
              Shopping across borders naturally involves sharing personal information — shipping addresses, names, phone numbers — with platforms and sellers. While most transactions go smoothly, it is wise to follow basic privacy hygiene to minimize unnecessary exposure.
            </p>
            <p className="text-sm md:text-base leading-relaxed">
              First, create a shopping-specific email address rather than using your primary personal or work email. This keeps promotional messages and order confirmations separate and reduces the chance of your main inbox being flooded with marketing emails from platforms you purchase from.
            </p>
            <p className="text-sm md:text-base leading-relaxed">
              Second, when using an agent service, your personal address is typically only shared with the agent — not directly with individual sellers. The agent acts as a privacy buffer between you and multiple sellers. This is an underappreciated benefit of using an intermediary service that many shoppers overlook.
            </p>
            <p className="text-sm md:text-base leading-relaxed">
              Third, be mindful of the information you share in community forums, Discord servers, or Reddit threads related to the Superbuy Spreadsheet. Avoid posting order numbers, tracking links, or screenshots that contain your personal details. The community is generally supportive, but public posts are visible to everyone — including bad actors looking for targets.
            </p>
          </section>

          {/* Section 8: FAQ */}
          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
              Frequently Asked Safety Questions
            </h2>
            <div className="space-y-5">
              <div>
                <h3 className="font-semibold text-foreground-900 text-sm md:text-base mb-1">
                  Can someone hack my account through a spreadsheet link?
                </h3>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  No. The Superbuy Spreadsheet is a standard Google Sheets document. Clicking links inside it cannot install software or compromise your accounts. The only risk is clicking through to a non-legitimate product page, which you can protect against by checking URLs before logging in or entering payment details. For a complete introduction, see our <Link to="/superbuy-spreadsheet-guide" className="text-primary-600 hover:text-primary-700 font-medium underline underline-offset-4">Complete Guide</Link>.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground-900 text-sm md:text-base mb-1">
                  What should I do if I receive a damaged or incorrect item?
                </h3>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  If you used an agent and the issue was caught at QC, refuse the item and request an exchange or refund before international shipping. If the item has already shipped internationally, contact the platform&rsquo;s customer support and file a dispute with photographic evidence. Most major platforms have a dispute resolution process that favors buyers when clear evidence is provided.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground-900 text-sm md:text-base mb-1">
                  Is it safer to use a shopping agent or buy directly?
                </h3>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  For shoppers outside the platform&rsquo;s primary shipping regions, using an agent is generally safer and more convenient. Agents provide QC photos, consolidate multiple orders into one shipment, handle customs documentation, and add a layer of accountability between you and the seller. Direct purchasing is simpler but leaves you with fewer safety nets if something goes wrong.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground-900 text-sm md:text-base mb-1">
                  How do I know if a product listed in the spreadsheet is authentic?
                </h3>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  The spreadsheet community typically labels products clearly when they are replicas or inspired designs. However, community labeling is not foolproof. Cross-reference the price — if it seems too good to be true, it probably is. Check QC photos carefully, compare against official product images, and read community notes in the spreadsheet for additional context. The <Link to="/superbuy-spreadsheet-categories" className="text-primary-600 hover:text-primary-700 font-medium underline underline-offset-4">Categories Guide</Link> has more brand-specific authentication advice.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground-900 text-sm md:text-base mb-1">
                  Are there any regions where shipping is riskier?
                </h3>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  Customs regulations and import duties vary significantly by country. Some regions have stricter customs inspection processes that can cause delays or additional fees. Our <Link to="/superbuy-spreadsheet-shipping-guide" className="text-primary-600 hover:text-primary-700 font-medium underline underline-offset-4">Shipping Guide</Link> includes a detailed country-by-country breakdown of customs considerations and shipping reliability data.
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="rounded-lg bg-primary-100/60 p-6 md:p-8 text-center my-10">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-3">
              Ready to Shop Safely?
            </h2>
            <p className="text-sm md:text-base text-foreground-700 mb-5 max-w-lg mx-auto leading-relaxed">
              Apply these safety principles to every purchase. Browse curated categories with confidence on the main Superbuy platform.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 rounded-full bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap"
              >
                Superbuy Sheet
                <i className="ri-arrow-right-up-line ml-1.5 text-xs"></i>
              </a>
              <Link
                to="/"
                className="px-6 py-3 rounded-full bg-background-100 text-foreground-900 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap"
              >
                Back to Home
                <i className="ri-arrow-left-line ml-1.5 text-xs"></i>
              </Link>
            </div>
          </section>

          {/* Internal Links to Other Articles */}
          <div className="border-t border-background-200 pt-8">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">Continue Reading</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Link to="/superbuy-spreadsheet-guide" className="flex items-start gap-3 p-4 rounded-lg bg-background-100 hover:bg-background-200 transition-colors">
                <i className="ri-book-open-line text-accent-500 text-lg mt-0.5"></i>
                <div>
                  <span className="text-sm font-semibold text-foreground-900 block">Complete Beginner Guide</span>
                  <span className="text-xs text-foreground-500">Everything from first access to first order</span>
                </div>
              </Link>
              <Link to="/superbuy-spreadsheet-shipping-guide" className="flex items-start gap-3 p-4 rounded-lg bg-background-100 hover:bg-background-200 transition-colors">
                <i className="ri-ship-line text-secondary-500 text-lg mt-0.5"></i>
                <div>
                  <span className="text-sm font-semibold text-foreground-900 block">Shipping Guide</span>
                  <span className="text-xs text-foreground-500">Costs, carriers, customs, and delivery strategies</span>
                </div>
              </Link>
            </div>
          </div>

          <div className="text-center pt-4 pb-8">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors underline underline-offset-4">
              <i className="ri-arrow-left-line"></i>
              Back to Superbuy Spread Hub Home
            </Link>
          </div>
        </div>
      </article>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Is Superbuy Spreadsheet Safe? The Complete 2026 Security Guide",
          "description": "A thorough, no-nonsense breakdown of Superbuy Spreadsheet safety covering payment protection, seller verification, privacy practices, QC photo strategies, common scams, and how to shop with complete confidence on international platforms.",
          "image": "https://readdy.ai/api/search-image?query=Luxury%20leather%20wallet%20and%20watch%20and%20sunglasses%20displayed%20on%20slate%20gray%20stone%20surface%2C%20soft%20side%20lighting%20creating%20elegant%20reflections%2C%20bronze%20and%20brown%20and%20gunmetal%20accents%2C%20sophisticated%20product%20photography%20with%20shallow%20depth%20of%20field%2C%20modern%20minimalist%20luxury%20boutique%20feel%2C%20crisp%20and%20refined%20composition&width=600&height=800&seq=about-img-2-v2&orientation=portrait",
          "author": {
            "@type": "Organization",
            "name": "Superbuy Spread Hub"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Superbuy Spread Hub",
            "logo": {
              "@type": "ImageObject",
              "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67defc9f4dfae2c04004e8ea78b88107.png"
            }
          },
          "datePublished": "2026-07-13",
          "dateModified": "2026-07-13",
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": "https://superbuyspread.homes/superbuy-spreadsheet-safety-guide"
          }
        }) }}
      />
    </div>
  );
}